import React, {useContext, useEffect, useState} from "react";
import { Button, Grid, IconButton, Typography, useTheme } from "@mui/material";
import { CheckCircle, Send } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { IOtpCheckPayload, IOtpSendPayload, IOtpValidationForm } from "../../../models/models";
import { bookingDataValueState, otpValidationValueState } from "../../state";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { SET_SNACKBAR } from "../../../common/utils";
import { fetchCheckOtp, fetchSendOtp } from "../../services/http.services";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { deepClone } from "../../../../../util/state/commonFunctions";
import { FormTextField } from "../../../common/form-fields-wrappers";
import CustomLoader from "../../../../../util/component/CustomLoader";

const OtpValidationData = (props: any) => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);
	const title = props.title

	const { dispatchGlobalData } = useContext(GlobalContext);

	const [action, setAction] = useState<{name: 'SEND' | 'VALIDATE', type: 'EMAIL' | 'MOBILE', param: any} | null>(null);

	const referenceProp = props.referenceProp + (props.ind !== undefined ? '.' + props.ind : '');

	const [optValueState, setOptValueState] = useRecoilState(otpValidationValueState);

	const bookingDataValue: any = useRecoilValue(bookingDataValueState);

	const otpValidationForm: any = useFormContext<IOtpValidationForm>();

	// @ts-ignore
	const otp = {
		email: props.ind !== undefined ? (optValueState?.additionalDrivers[props.ind] as any)?.email : (optValueState.mainDriver as any ).email,
		mobile: props.ind !== undefined ? (optValueState?.additionalDrivers[props.ind] as any)?.mobile : (optValueState.mainDriver as any ).mobile
	};

	const handleSendEmailOTP = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		otpValidationForm.setValue(`${referenceProp}.emailOtp`, '');

		let driver;
		if (referenceProp === 'mainDriver') {
			driver = bookingDataValue.raOwner;
		}
		else {
			if (props.ind === 0) {
				driver = bookingDataValue.firstDriver;
			} else {
				driver = bookingDataValue.driver;
			}
		}

		let payload: IOtpSendPayload = {
			idRentalAgreement: bookingDataValue.idRentalAgreement,
			idDriver: driver.cbId,
			type: 'EMAIL',
			driverEmail: driver.email,
			driverName: driver.firstName + ' ' + driver.lastName
		}

		setAction({
			name: 'SEND',
			type: 'EMAIL',
			param: payload
		});
	}

	const handleCheckEmailOTP = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		let driver;
		if (referenceProp === 'mainDriver') {
			driver = bookingDataValue.raOwner;
		}
		else {
			if (props.ind === 0) {
				driver = bookingDataValue.firstDriver;
			} else {
				driver = bookingDataValue.driver;
			}
		}

		const payload: IOtpCheckPayload = {
			idRentalAgreement: bookingDataValue.idRentalAgreement,
			idDriver: driver.cbId,
			type: 'EMAIL',
			// @ts-ignore
			otpCode: otpValidationForm.getValues(`${referenceProp}.emailOtp`)
		}

		setAction({
			name: 'VALIDATE',
			type: 'EMAIL',
			param: payload
		});
	}

	const handleSendMobileOTP = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		otpValidationForm.setValue(`${referenceProp}.mobileOtp`, '');

		let driver;
		if (referenceProp === 'mainDriver') {
			driver = bookingDataValue.raOwner;
		}
		else {
			if (props.ind === 0) {
				driver = bookingDataValue.firstDriver;
			} else {
				driver = bookingDataValue.driver;
			}
		}

		let payload: IOtpSendPayload = {
			idRentalAgreement: bookingDataValue.idRentalAgreement,
			idDriver: driver.cbId,
			type: 'MOBILE',
			driverPhone: '+' + driver.prefix.description + driver.mobile,
			driverName: driver.firstName + ' ' + driver.lastName
		};

		setAction({
			name: 'SEND',
			type: 'EMAIL',
			param: payload
		});
	}

	const handleCheckMobileOTP = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		let driver;
		if (referenceProp === 'mainDriver') {
			driver = bookingDataValue.raOwner;
		}
		else {
			if (props.ind === 0) {
				driver = bookingDataValue.firstDriver;
			} else {
				driver = bookingDataValue.driver;
			}
		}

		const payload: IOtpCheckPayload = {
			idRentalAgreement: bookingDataValue.idRentalAgreement,
			idDriver: driver.cbId,
			type: 'MOBILE',
			// @ts-ignore
			otpCode: otpValidationForm.getValues(`${referenceProp}.mobileOtp`)
		}

		setAction({
			name: 'VALIDATE',
			type: 'MOBILE',
			param: payload
		});
	}

	const sendOtpMutation = useMutation(
		fetchSendOtp,
		{
			onSuccess: (response: any) => {
				// noop
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	const checkOtpMutation = useMutation(
		fetchCheckOtp,
		{
			onSuccess: (response: any) => {
				if (!!response) { // response : {isValid: boolean, isExpired: boolean}
					const otpState = deepClone(optValueState);

					if (action?.type === 'EMAIL') {
						if (props.referenceProp === 'mainDriver') {
							otpState[referenceProp].email = response.isValid;
						} else {
							otpState[props.referenceProp][props.ind].email = response.isValid;
						}

						if (response.isValid) {
							otpValidationForm.setValue(`${referenceProp}.emailOtp`, '');
						}
					} else { // 'MOBILE'
						if (props.referenceProp === 'mainDriver') {
							otpState[referenceProp].mobile = response.isValid;
						} else {
							otpState[props.referenceProp][props.ind].mobile = response.isValid;
						}

						if (response.isValid) {
							otpValidationForm.setValue(`${referenceProp}.mobileOtp`, '');
						}
					}
					setOptValueState(otpState);

					if (response.isExpired) {
						dispatchGlobalData({
							type: 'SET_SNACKBAR',
							payload: {
								snackbar: {
									message: t('OTP Expired'),
									type: snackbarTypes.error,
									show: true,
									autoClose: true,
								}
							}
						});
					}
				} else {
					dispatchGlobalData({
						type: 'SET_SNACKBAR',
						payload: {
							snackbar: {
								message: t('translation:error.tryLater'),
								type: snackbarTypes.error,
								show: true,
								autoClose: true,
							}
						}
					});
				}
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	useEffect(()=> {
		if (action?.name === 'SEND') {
			sendOtpMutation.mutate(action?.param as any);
		}
		else if (action?.name === 'VALIDATE') {
			checkOtpMutation.mutate(action?.param as any);
		}
	}, [action])

	return (
		<>
			<Grid container columnSpacing={2}>
			<Grid item xs={12}>
				<Typography variant="h6">
					{title}
				</Typography>
			</Grid>

			<Grid container item xs={12} lg={6} columnSpacing={2}>
				<Grid item xs={12} sm={6}>
					<FormTextField<IOtpValidationForm>
						name={`${referenceProp}.name`}
						label={t("otpValidation.form.label.name")}
						disabled
						control={otpValidationForm.control}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormTextField<IOtpValidationForm>
						name={`${referenceProp}.surname`}
						label={t("otpValidation.form.label.surname")}
						disabled
						control={otpValidationForm.control}
					/>
				</Grid>

			</Grid>
			<Grid container item xs={12} lg={6} columnSpacing={2}>

				<Grid item xs={10} md={5}>
					<FormTextField<IOtpValidationForm>
						name={`${referenceProp}.email`}
						label={t("otpValidation.form.label.email")}
						disabled
						control={otpValidationForm.control}
					/>
				</Grid>
				<Grid item xs={2} md={1} sx={{display: "flex", alignItems: 'flex-end'}}>
					<IconButton size="small"
								onClick={handleSendEmailOTP}
								disabled={otp.email === true}>
						<Send />
					</IconButton>
				</Grid>

				<Grid item xs={12} md={6} sx={{display: "flex", alignItems: "flex-end"}}>
					<FormTextField<IOtpValidationForm>
						name={`${referenceProp}.emailOtp`}
						label={t("otpValidation.form.label.emailOtp")}
						control={otpValidationForm.control}
						disabled={otp.email === true}
					/>


					{otp.email === undefined &&
						<CheckCircle sx={{color: theme.palette.neutral.light, ml: theme.spacing(2) , mr: theme.spacing(2)}} />
					}
					{otp.email === true &&
						<CheckCircle sx={{color: theme.palette.success.main, ml: theme.spacing(2) , mr: theme.spacing(2)}} />
					}
					{otp.email === false &&
						<CheckCircle sx={{color: theme.palette.error.main, ml: theme.spacing(2) , mr: theme.spacing(2)}} />
					}
					<Button color={"accent"}
							onClick={handleCheckEmailOTP}
							disabled={otp.email === true}>
						{t('otpValidation.form.button.verifyOtp')}
					</Button>
				</Grid>


				<Grid item xs={10} md={5}>
					<FormTextField<IOtpValidationForm>
						name={`${referenceProp}.mobile`}
						label={t("otpValidation.form.label.mobile")}
						disabled
						control={otpValidationForm.control}
					/>
				</Grid>
				<Grid item xs={2} md={1} sx={{display: "flex", alignItems: "flex-end"}}>
					<IconButton size="small"
								onClick={handleSendMobileOTP}
								disabled={otp.mobile === true}>
						<Send />
					</IconButton>
				</Grid>

				<Grid item xs={12} md={6} sx={{display: "flex", alignItems: "flex-end"}}>
					<FormTextField<IOtpValidationForm>
						name={`${referenceProp}.mobileOtp`}
						label={t("otpValidation.form.label.mobileOtp")}
						control={otpValidationForm.control}
						disabled={otp.mobile === true}
					/>
					{
						otp.mobile === undefined &&
						<CheckCircle sx={{color: theme.palette.neutral.light, ml: theme.spacing(2), mr: theme.spacing(2)}} />
					}
					{
						otp.mobile === true &&
						<CheckCircle sx={{color: theme.palette.success.main , ml: theme.spacing(2), mr: theme.spacing(2)}} />
					}
					{
						otp.mobile === false &&
						<CheckCircle sx={{color: theme.palette.error.main, ml: theme.spacing(2), mr: theme.spacing(2)}} />
					}
					<Button	color={"accent"}
							   onClick={handleCheckMobileOTP}
							   disabled={otp.mobile === true}
					>
						{t('otpValidation.form.button.verifyOtp')}
					</Button>
				</Grid>

			</Grid>
		</Grid>

			<CustomLoader loading={checkOtpMutation.isLoading || sendOtpMutation.isLoading} />
		</>
	);
}

export default OtpValidationData;
